/**
 * Signing in with Google, loaded only when it is actually needed.
 *
 * The Firebase SDK is around 250 KB. Loading it in the main bundle would make
 * every child wait for it on every launch, to support a screen a parent sees
 * roughly once. So it is a dynamic `import()`, the same approach the barcode
 * reader uses in `barcode.ts` and for the same reason: a feature used rarely
 * should not be paid for constantly.
 *
 * **The configuration comes from the server, not from a build flag.** One
 * bundle runs on a laptop and in the cloud, and the server is the only thing
 * that knows which it is. A `VITE_` variable would mean two bundles that can
 * be built the wrong way round, and the wrong way round here means a sign-in
 * screen that quietly does nothing.
 *
 * **These values are not secret.** A Firebase web config identifies a project;
 * it authorises nothing, which is why Google prints it in its own samples.
 * What keeps this app private is the list of allowed addresses on the server,
 * where a browser cannot reach it. Do not be tempted to "protect" this.
 */

export type FirebaseConfig = {
  apiKey: string
  authDomain: string
  projectId: string
  appId: string
}

/** Kept between calls, because initializing Firebase twice throws. */
let started: { auth: unknown; provider: unknown } | null = null

async function start(config: FirebaseConfig) {
  if (started) return started
  const [{ initializeApp, getApps }, authModule] = await Promise.all([
    import('firebase/app'),
    import('firebase/auth'),
  ])
  const app = getApps().length ? getApps()[0] : initializeApp(config)
  const auth = authModule.getAuth(app)
  const provider = new authModule.GoogleAuthProvider()
  // Always ask which account, rather than silently reusing the one the
  // browser last used. A shared family laptop is the normal case here, and
  // "it signed me in as the wrong person" is a confusing failure.
  provider.setCustomParameters({ prompt: 'select_account' })
  started = { auth, provider }
  return started
}

/**
 * Open Google's sign-in window and return the ID token it gives back.
 *
 * The token is what goes to our server, which verifies the signature with
 * Google and then checks the address against the allow list. Nothing is
 * decided here in the browser — this function's only job is to obtain the
 * token and hand it over.
 */
export async function signInWithGoogle(config: FirebaseConfig): Promise<string> {
  const { auth, provider } = await start(config)
  const { inMemoryPersistence, setPersistence, signInWithPopup, signOut } = await import('firebase/auth')
  // Keep nothing of Google's on the device (Sam, 22 Oct 2026: the boys'
  // iPads must not hold his account). Our server only needs this one ID
  // token, and after that its own cookie is the sign-in. So Firebase is told
  // to hold the Google user in memory only, and to drop it straight away,
  // instead of saving it in the browser where it would sit indefinitely.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await setPersistence(auth as any, inMemoryPersistence)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = await signInWithPopup(auth as any, provider as any)
  const token = await result.user.getIdToken()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await signOut(auth as any).catch(() => undefined)
  return token
}

/**
 * Forget the Google session as well as ours.
 *
 * Without this, signing out of Word Sprout leaves the browser still signed in
 * to Google, so the next tap of "Sign in with Google" goes straight back in
 * without asking. That looks exactly like sign-out being broken.
 */
export async function signOutOfGoogle(config: FirebaseConfig): Promise<void> {
  try {
    const { auth } = await start(config)
    const { signOut } = await import('firebase/auth')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await signOut(auth as any)
  } catch {
    // Our own cookie is already gone by the time this runs, so a failure
    // here is untidy rather than serious. Never block sign-out on it.
  }
}

/** What went wrong, in a sentence a parent can act on. */
export function explainSignInProblem(error: unknown): string {
  const code = (error as { code?: string } | null)?.code ?? ''
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
    return 'The Google window was closed before signing in.'
  }
  if (code === 'auth/popup-blocked') {
    return 'The browser blocked the Google window. Allow pop-ups for this site and try again.'
  }
  if (code === 'auth/network-request-failed') {
    return 'No connection to Google. Check the network and try again.'
  }
  if (code === 'auth/unauthorized-domain') {
    // Worth its own message: it is a setup mistake, not a parent mistake,
    // and the fix is in the Firebase console rather than on this screen.
    return 'This web address is not authorised for sign-in yet.'
  }
  return 'That did not work. Check the connection and try again.'
}
